// File: src/components/modals/EditProfileModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { getAuth } from "firebase/auth";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { useProfile } from "../../context/ProfileContext";
import { uploadFile } from "../../services/fileUpload";
import UserProfilePicture from '../profile/UserProfilePicture';
import toast from 'react-hot-toast';
import { compressImage } from '../../services/fileCompression';

const STATE_OPTIONS = [
  { code: 'NSW', label: 'New South Wales (NSW)' },
  { code: 'VIC', label: 'Victoria (VIC)' },
  { code: 'QLD', label: 'Queensland (QLD)' },
  { code: 'WA', label: 'Western Australia (WA)' },
  { code: 'SA', label: 'South Australia (SA)' },
  { code: 'TAS', label: 'Tasmania (TAS)' },
  { code: 'ACT', label: 'Australian Capital Territory (ACT)' },
  { code: 'NT', label: 'Northern Territory (NT)' },
  { code: 'INT', label: 'Outside Australia (International)' }
];

const EditProfileModal = ({ isOpen, onClose }) => {
  const { activeProfile, switchProfile } = useProfile();
  
  // Basic Profile Form States
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [bio, setBio] = useState('');
  const [picturePreview, setPicturePreview] = useState(null);
  const [pictureFile, setPictureFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [handleError, setHandleError] = useState('');
  
  // 🌟 Smart Location States
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [suburbSearchQuery, setSuburbSearchQuery] = useState('');
  const [allSuburbsInState, setAllSuburbsInState] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedSuburb, setSelectedSuburb] = useState(null);
  const [isLoadingSuburbs, setIsLoadingSuburbs] = useState(false);

  const isArtist = activeProfile?.type === 'artist';
  const fileInputRef = useRef(null);

  // Sync modal state with active profile on open
  useEffect(() => {
    if (isOpen && activeProfile) {
      const name = activeProfile.type === 'artist' ? activeProfile.artistName : activeProfile.displayName;
      setDisplayName(name || '');
      setHandle(activeProfile.handle || '');
      setCity(activeProfile.city || '');
      setState(activeProfile.state || '');
      setSuburbSearchQuery(activeProfile.city || '');
      setSelectedSuburb(activeProfile.city ? { suburb: activeProfile.city } : null);
      setBio(activeProfile.bio || '');
      setPicturePreview(activeProfile.photoURL);
      setPictureFile(null);
      setHandleError(''); 
    }
  }, [isOpen, activeProfile]);

  // 🌟 Fetch Suburb CSV Registry when State changes
  useEffect(() => {
    if (!state || state === 'INT' || state === 'Undisclosed') {
      setAllSuburbsInState([]);
      return;
    }

    const fetchStateSuburbs = async () => {
      setIsLoadingSuburbs(true);
      try {
        const response = await fetch(
          `https://cdn.jsdelivr.net/gh/schappim/australian-postcodes@master/data/by-state/${state}.csv`
        );
        const csvText = await response.text();
        const lines = csvText.split('\n');
        const parsed = lines.slice(1).map(line => {
          const cols = line.split(',');
          return { suburb: cols[1]?.trim(), state: cols[2]?.trim() };
        }).filter(item => item.suburb);

        const unique = Array.from(new Map(parsed.map(item => [item.suburb, item])).values());
        setAllSuburbsInState(unique);
      } catch (err) {
        console.error("EDIT PROFILE: Suburb sync error", err);
      } finally {
        setIsLoadingSuburbs(false);
      }
    };

    fetchStateSuburbs();
  }, [state]);

  // 🌟 Filter Suburb Autocomplete Suggestions
  useEffect(() => {
    if (!suburbSearchQuery.trim() || selectedSuburb?.suburb === suburbSearchQuery) {
      setFilteredSuggestions([]);
      return;
    }
    const query = suburbSearchQuery.toLowerCase();
    const matches = allSuburbsInState
      .filter(item => item.suburb.toLowerCase().startsWith(query))
      .slice(0, 6);
    setFilteredSuggestions(matches);
  }, [suburbSearchQuery, allSuburbsInState, selectedSuburb]);

  if (!isOpen) return null;

  const handleHandleChange = (e) => {
    const newHandle = e.target.value.toLowerCase();
    const validHandleRegex = /^[a-z0-9_]*$/;

    if (validHandleRegex.test(newHandle)) {
      setHandle(newHandle);
      setHandleError('');
    } else {
      setHandleError('Handles can only use letters, numbers, and underscores.');
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (handleError) return toast.error(handleError); 

    if (!handle || !handle.trim()) {
      return toast.error("Handle cannot be left blank.");
    }

    const auth = getAuth();
    if (!auth.currentUser || !activeProfile) {
      return toast.error("You must be logged in to save changes.");
    }
    
    setIsLoading(true);

    try {
      let newPhotoURL = activeProfile.photoURL;
      const token = await auth.currentUser.getIdToken();

      // Image Upload Handling
      if (pictureFile) {
        if (activeProfile.type === 'artist') {
          const generateUrlResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/generateArtistUploadUrl", {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              artistId: activeProfile.id,
              fileName: `profile_picture.${pictureFile.name.split('.').pop()}`,
              contentType: pictureFile.type
            })
          });

          if (!generateUrlResponse.ok) {
            throw new Error((await generateUrlResponse.json()).error || "Could not get upload permission.");
          }

          const { uploadUrl, finalPath } = await generateUrlResponse.json();
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': pictureFile.type },
            body: pictureFile
          });
          
          const storage = getStorage();
          newPhotoURL = await getDownloadURL(ref(storage, finalPath));

        } else {
          const userId = auth.currentUser.uid;
          const fileExtension = pictureFile.name.split('.').pop();
          const fullPath = `profile_pictures/${userId}/profile_picture.${fileExtension}`;
          const result = await uploadFile(pictureFile, fullPath);
          newPhotoURL = result.fileURL;
        }
      }

      // Final Location Values Calculation
      const finalCity = state === 'INT' ? '' : (selectedSuburb?.suburb || suburbSearchQuery.trim() || city);

      const payload = {
        profileId: activeProfile.id,
        profileType: activeProfile.type,
        newName: displayName,
        newHandle: handle,
        newCity: finalCity,
        newState: state,
        newBio: bio,
        newPhotoURL: newPhotoURL,
        newName_lowercase: displayName.toLowerCase(),
        newHandle_lowercase: handle.toLowerCase(),
      };

      const updateResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/updateProfileDetails", {
        method: "POST",
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`},
        body: JSON.stringify(payload)
      });

      if (!updateResponse.ok) {
        throw new Error((await updateResponse.json()).error || "Failed to update profile.");
      }
      
      const updatedProfile = await updateResponse.json();
      switchProfile(updatedProfile);

      toast.success("Profile updated successfully!");
      onClose();

    } catch (error) {
      console.error("Error updating profile:", error.message);
      toast.error(`Failed to update profile: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
    
  const openFileInput = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size / 1024 / 1024 > 10) {
      toast.error("Please select an image smaller than 10 MB.");
      return;
    }

    setIsCompressing(true);
    setPicturePreview(URL.createObjectURL(file));

    try {
      const compressedFile = await compressImage(file, { maxSizeMB: 0.5 });
      setPictureFile(compressedFile); 
    } catch (error) {
      toast.error("Failed to compress image.");
      setPictureFile(file);
    } finally {
      setIsCompressing(false); 
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1000] p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 w-full max-w-sm my-4 max-h-[90vh] overflow-y-auto text-slate-900 dark:text-white shadow-2xl">
        <h2 className="text-xl font-bold mb-4">Edit Profile</h2>
        <form onSubmit={handleSaveChanges}>
          
          {/* Profile Picture Section */}
          <div className="flex justify-center mb-4">
            <UserProfilePicture 
              photoURL={picturePreview} 
              sizeClass="w-24 h-24" 
              isCompressing={isCompressing}
            />
          </div>
          <button 
            type="button" 
            onClick={openFileInput} 
            className="w-full bg-gray-200 dark:bg-zinc-800 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded-xl mb-4 hover:bg-gray-300 transition-colors text-xs uppercase tracking-wider"
          >
            {pictureFile ? 'Change Picture' : 'Edit Profile Picture'}
          </button>
          <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
          
          <hr className="my-4 border-gray-200 dark:border-zinc-800" />
          
          {/* Display Name */}
          <div className="mb-4">
            <label className="block text-xs font-bold uppercase tracking-wider mb-1" htmlFor="displayName">
              {isArtist ? 'Artist Name' : 'Display Name'}
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="shadow-sm border rounded-xl w-full py-2 px-3 text-sm dark:bg-zinc-800 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>

          {/* Handle */}
          <div className="mb-4">
            <label className="block text-xs font-bold uppercase tracking-wider mb-1" htmlFor="handle">
              Handle
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-sm">
                @
              </span>
              <input
                id="handle"
                type="text"
                value={handle}
                onChange={handleHandleChange}
                className="shadow-sm border rounded-xl w-full py-2 px-3 pl-8 text-sm dark:bg-zinc-800 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            {handleError && <p className="text-red-500 text-xs italic mt-1">{handleError}</p>}
          </div>

          {/* 🌟 LOCATION HUB: STATE & SUBURB AUTOCOMPLETE */}
          <div className="space-y-3 mb-4">
            {/* 1. State Selector */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1" htmlFor="state">
                State / Region
              </label>
              <select
                id="state"
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  setSelectedSuburb(null);
                  setSuburbSearchQuery('');
                  setCity('');
                }}
                className="shadow-sm border rounded-xl w-full py-2 px-3 text-sm dark:bg-zinc-800 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="">Select Region...</option>
                {STATE_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 2. Suburb Autocomplete (Rendered for Australian States) */}
            {state && state !== 'INT' && (
              <div className="relative">
                <label className="block text-xs font-bold uppercase tracking-wider mb-1" htmlFor="suburb">
                  City / Suburb
                </label>
                <input
                  id="suburb"
                  type="text"
                  placeholder={isLoadingSuburbs ? "Downloading registry..." : "Type town or suburb..."}
                  disabled={isLoadingSuburbs}
                  value={suburbSearchQuery}
                  onChange={(e) => {
                    setSelectedSuburb(null);
                    setSuburbSearchQuery(e.target.value);
                    setCity(e.target.value);
                  }}
                  className="shadow-sm border rounded-xl w-full py-2 px-3 text-sm dark:bg-zinc-800 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
                />

                {/* Autocomplete Suggestions Menu */}
                {filteredSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 rounded-xl shadow-xl z-50 max-h-36 overflow-y-auto bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 p-1">
                    {filteredSuggestions.map((item, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setSelectedSuburb(item);
                          setSuburbSearchQuery(item.suburb);
                          setCity(item.suburb);
                          setFilteredSuggestions([]);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs font-bold rounded-lg hover:bg-pink-500 hover:text-white transition-colors"
                      >
                        {item.suburb}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bio Textarea */}
          <div className="mb-4">
            <label className="block text-xs font-bold uppercase tracking-wider mb-1" htmlFor="bio">
              Bio
            </label>
            <textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              maxLength={500}
              className="shadow-sm border rounded-xl w-full py-2 px-3 text-sm dark:bg-zinc-800 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Tell us a bit about yourself..."
            />
            <p className="text-right text-[10px] text-gray-500 mt-0.5">{bio.length}/500</p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-2 pt-2">
            <button 
              type="button" 
              onClick={onClose} 
              disabled={isLoading} 
              className="bg-gray-200 dark:bg-zinc-800 hover:bg-gray-300 text-gray-800 dark:text-gray-200 font-bold py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isLoading} 
              className="bg-pink-500 hover:bg-pink-600 text-white font-bold py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider shadow-md transition-colors"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;